import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";
import { persistLead, captureBlockedLead } from "@/lib/lead-fallback.server";
import { internalLeadEmail, LEAD_INBOX, LEAD_NOTIFY, sendEmail } from "@/lib/email/resend.server";
import { checkRateLimit, getClientIp } from "@/lib/rate-limit.server";

const callbackSchema = z.object({
  name: z.string().trim().min(1, "Namn krävs").max(100),
  phone: z
    .string()
    .trim()
    .min(6, "Ange ett giltigt telefonnummer")
    .max(32)
    .regex(/^[\d\s+\-()]+$/, "Endast siffror och +-()"),
  source_path: z.string().max(500).optional().nullable(),
  session_id: z.string().uuid().nullable().optional(),
  visitor_id: z.string().uuid().nullable().optional(),
  user_agent: z.string().max(500).optional().nullable(),
});

export const submitCallback = createServerFn({ method: "POST" })
  .inputValidator((input) => callbackSchema.parse(input))
  .handler(async ({ data }) => {
    const ip = getClientIp(getRequest());
    if (!checkRateLimit({ scope: "callback", keys: [ip, data.phone], limit: 5, windowMs: 60 * 60 * 1000 })) {
      await captureBlockedLead({ flow: "callback", reason: "rate-limit",
        name: data.name,
        phone: data.phone,
        source_path: data.source_path ?? null,
        user_agent: data.user_agent ?? null,
        session_id: data.session_id ?? null,
        visitor_id: data.visitor_id ?? null,
      });
      return { ok: false as const, error: "För många försök. Försök igen om en stund." };
    }
    try {
      const { ok } = await persistLead("callback_requests", {
        name: data.name,
        phone: data.phone,
        source_path: data.source_path ?? null,
        session_id: data.session_id ?? null,
        visitor_id: data.visitor_id ?? null,
        user_agent: data.user_agent ?? null,
      }, {
        source: "callback",
        name: data.name,
        source_path: data.source_path ?? null,
        session_id: data.session_id ?? null,
        visitor_id: data.visitor_id ?? null,
      });
      // Vid fel har persistLead redan skrivit en fallback-rad, men vi låter
      // besökaren veta att något gick fel så hen kan höra av sig på annat sätt.
      if (!ok) {
        return { ok: false as const, error: "Kunde inte ta emot just nu. Försök igen." };
      }
    } catch (e) {
      console.error("[callback] insert unexpected:", e);
      return { ok: false as const, error: "Kunde inte ta emot just nu. Försök igen." };
    }

    const internal = internalLeadEmail({
      subject: `Ring-mig: ${data.name} (${data.phone})`,
      heading: "Ny ring-mig-förfrågan från footern",
      fields: {
        Namn: data.name,
        Telefon: data.phone,
        Sida: data.source_path ?? "—",
      },
    });
    await sendEmail({
      to: LEAD_NOTIFY,
      subject: internal.subject,
      html: internal.html,
      logContext: { kind: "internal_lead", leadSource: "footer_callback" },
    }).catch((e) => console.error("[callback] mail failed", e));

    return { ok: true as const };
  });
